import Icon from "@/v2-ui/icon"
import TranslationSidebarCategoryItem from "@/v2-backoffice-client/translation/sidebar/TranslationSidebarCategoryItem"
import TranslationSidebarInfo from "@/v2-backoffice-client/translation/sidebar/TranslationSidebarInfo"
import {
  matchesCategoryQuery,
  type SidebarInfo,
} from "@/v2-backoffice-client/translation/sidebar/translation.sidebar.utils"
import type { TranslationCategory } from "@/v2-backoffice-client/translation/translation.types"

const SECTION_LABELS: Record<TranslationCategory["section"], string> = {
  ui: "App UI",
  data: "Data labels",
}

const CATEGORY_SECTION_INFO: Record<
  TranslationCategory["section"],
  SidebarInfo
> = {
  ui: {
    title: "App UI",
    body: "The same catalog as All texts, sliced by app area (map, dashboards, settings, …). The count reads complete/total texts in that area.",
  },
  data: {
    title: "Data labels",
    body: "Catalog slices for data-driven labels: column names, enum values and other texts that describe data rather than UI chrome.",
  },
}

export type TranslationSidebarCategorySectionProps = {
  section: TranslationCategory["section"]
  categories: TranslationCategory[]
  // Already trimmed + lowercased by the owner (shared across both sections,
  // no reason to redo it here).
  query: string
  isCollapsed: boolean
  activeCategoryKey: string | null
  // Whether the catalog view (not some other view) is the one currently
  // showing — a category can only read as active when the operator is
  // actually looking at the catalog table filtered to it.
  isCatalogActive: boolean
  onToggle(): void
}

// One collapsible category group ("App UI" or "Data labels") in the
// category tree below the pinned sections and search box.
function TranslationSidebarCategorySection(
  props: TranslationSidebarCategorySectionProps,
) {
  const {
    section,
    categories,
    query,
    isCollapsed,
    activeCategoryKey,
    isCatalogActive,
    onToggle,
  } = props

  if (categories.length === 0) return null

  const matching = query
    ? categories.filter((category) => matchesCategoryQuery(category, query))
    : categories
  const total = categories.reduce((sum, c) => sum + c.total, 0)
  const incomplete = categories.reduce((sum, c) => sum + c.incomplete, 0)
  // A live search always shows its matches, so filtering never hides
  // results behind a collapsed header.
  const isOpen = query !== "" || !isCollapsed

  return (
    <div className="translation-sidebar-section">
      <button
        type="button"
        data-feature-code="UI_BackofficeTranslation_Sidebar_SectionToggle"
        aria-expanded={isOpen}
        onClick={onToggle}
        className="translation-sidebar-section-header translation-sidebar-section-toggle"
      >
        <Icon src={isOpen ? "caret-down" : "caret-right"} size={11} />
        {SECTION_LABELS[section]}
        <TranslationSidebarInfo {...CATEGORY_SECTION_INFO[section]} />
        <span className="translation-sidebar-item-count ml-auto">
          {total - incomplete}
          <span className="text-muted-foreground">/{total}</span>
        </span>
      </button>
      {isOpen && matching.length === 0 ? (
        <p className="translation-sidebar-empty">No matching categories</p>
      ) : null}
      {isOpen &&
        matching.map((category) => (
          <TranslationSidebarCategoryItem
            key={category.key}
            category={category}
            isActive={isCatalogActive && activeCategoryKey === category.key}
          />
        ))}
    </div>
  )
}

export default TranslationSidebarCategorySection
